import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { authBackendEndPointToken } from '../app.config';
import { AuthService } from '../shared/auth/auth.service';

type PatientImportPreviewRow = {
  rowNumber: number;
  rowKey: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  autoApprove: boolean;
  isValid: boolean;
  errors: string[];
  warnings: string[];
};

type PatientImportPreviewResponse = {
  importBatchId: string;
  expiresAtUtc: string;
  totalRows: number;
  validRows: number;
  invalidRows: number;
  canConfirm: boolean;
  rows: PatientImportPreviewRow[];
};

type PatientImportConfirmResponse = {
  importBatchId: string;
  totalRows: number;
  succeededRows: number;
  failedRows: number;
  autoApprove: boolean;
  message: string;
  rows: Array<{
    rowNumber: number;
    rowKey: string;
    email: string;
    succeeded: boolean;
    mode: string;
    userId?: string;
    approvalId?: string;
    fhirId?: string;
    localPatientId?: string;
    warnings: string[];
    errors: string[];
  }>;
};

@Component({
  selector: 'app-patient-registration-import',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatCardModule,
    MatCheckboxModule,
    MatDividerModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatTableModule
  ],
  templateUrl: './patient-registration-import.component.html',
  styleUrls: ['./patient-registration-import.component.scss']
})
export class PatientRegistrationImportComponent {
  private readonly http = inject(HttpClient);
  private readonly snackBar = inject(MatSnackBar);
  private readonly authService = inject(AuthService);
  private readonly backendUrl = inject(authBackendEndPointToken);

  readonly templateUrl = 'assets/templates/patient-registration-import-template.xlsx';
  readonly previewColumns = ['rowNumber', 'name', 'email', 'phoneNumber', 'status', 'messages'];
  readonly resultColumns = ['rowNumber', 'email', 'mode', 'status', 'fhirId', 'messages'];

  selectedFile: File | null = null;
  preview: PatientImportPreviewResponse | null = null;
  result: PatientImportConfirmResponse | null = null;
  autoApprove = false;
  isUploading = false;
  isConfirming = false;

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.selectedFile = input.files?.[0] ?? null;
    this.preview = null;
    this.result = null;
  }

  uploadForPreview(): void {
    if (!this.selectedFile) {
      this.snackBar.open('Choose an Excel file first.', 'Close', { duration: 3000 });
      return;
    }

    const formData = new FormData();
    formData.append('file', this.selectedFile);
    formData.append('autoApprove', String(this.autoApprove));

    const organizationId = this.resolveOrganizationId();
    if (organizationId) {
      formData.append('organizationId', organizationId);
    }

    this.isUploading = true;
    this.http.post<PatientImportPreviewResponse>(`${this.backendUrl}/api/import/patients/preview`, formData)
      .subscribe({
        next: preview => {
          this.preview = preview;
          this.result = null;
          this.isUploading = false;
          const message = preview.invalidRows > 0
            ? `${preview.invalidRows} row(s) need attention.`
            : `${preview.validRows} row(s) ready to import.`;
          this.snackBar.open(message, 'Close', { duration: 3500 });
        },
        error: error => {
          this.isUploading = false;
          this.snackBar.open(error?.error?.message || 'Could not preview the import file.', 'Close', { duration: 5000 });
        }
      });
  }

  confirmImport(): void {
    if (!this.preview?.canConfirm) {
      this.snackBar.open('Fix invalid rows before confirming.', 'Close', { duration: 3500 });
      return;
    }

    this.isConfirming = true;
    this.http.post<PatientImportConfirmResponse>(
      `${this.backendUrl}/api/import/patients/${this.preview.importBatchId}/confirm`,
      { autoApprove: this.autoApprove }
    ).subscribe({
      next: result => {
        this.result = result;
        this.isConfirming = false;
        this.snackBar.open(result.message, 'Close', { duration: 4000 });
      },
      error: error => {
        this.isConfirming = false;
        const result = error?.error as PatientImportConfirmResponse | undefined;
        if (result?.rows) {
          this.result = result;
          this.snackBar.open(result.message || 'Import completed with failed rows.', 'Close', { duration: 5000 });
          return;
        }
        this.snackBar.open(error?.error?.message || 'Could not confirm the import.', 'Close', { duration: 5000 });
      }
    });
  }

  reset(): void {
    this.selectedFile = null;
    this.preview = null;
    this.result = null;
  }

  messages(row: { errors?: string[]; warnings?: string[] }): string {
    const errors = row.errors ?? [];
    const warnings = row.warnings ?? [];
    return [...errors, ...warnings].join(' | ');
  }

  private resolveOrganizationId(): string | null {
    const user = this.authService.user?.getValue?.() as Record<string, string | null | undefined> | null | undefined;
    return user?.['organizationId'] || user?.['OrganizationId'] || user?.['hospitalId'] || null;
  }
}
